/**
 * Styles Resources
 *
 * Provides access to CSS styles catalog including base styles,
 * accessibility features, and utility classes.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import stylesCatalog from '../data/styles-catalog.json' with { type: 'json' };

interface StylesModule {
  name: string;
  path: string;
  description: string;
  features?: unknown[];
  classes?: unknown[];
}

interface StylesCatalog {
  version: string;
  package: string;
  description: string;
  integration: {
    import: string;
    placement: string;
    example: string;
  };
  layers: {
    order: string[];
    description: string;
  };
  modules: StylesModule[];
  peerDependencies: Record<string, string>;
}

const styles = stylesCatalog as StylesCatalog;

/**
 * Register styles-related resources
 */
export function registerStylesResources(server: McpServer): void {
  // All styles
  server.resource('styles://all', 'styles://all', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(styles, null, 2),
      },
    ],
  }));

  // Integration guide
  server.resource('styles://integration', 'styles://integration', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            package: styles.package,
            integration: styles.integration,
            layers: styles.layers,
            peerDependencies: styles.peerDependencies,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Base styles module
  server.resource('styles://base', 'styles://base', async (uri) => {
    const baseModule = styles.modules.find((m) => m.name === 'base');
    return {
      contents: [
        {
          uri: uri.href,
          mimeType: 'application/json',
          text: JSON.stringify(baseModule, null, 2),
        },
      ],
    };
  });

  // Accessibility styles module
  server.resource('styles://accessibility', 'styles://accessibility', async (uri) => {
    const accessibilityModule = styles.modules.find((m) => m.name === 'accessibility');
    return {
      contents: [
        {
          uri: uri.href,
          mimeType: 'application/json',
          text: JSON.stringify(accessibilityModule, null, 2),
        },
      ],
    };
  });

  // Utility classes module
  server.resource('styles://utilities', 'styles://utilities', async (uri) => {
    const utilitiesModule = styles.modules.find((m) => m.name === 'utilities');
    return {
      contents: [
        {
          uri: uri.href,
          mimeType: 'application/json',
          text: JSON.stringify(utilitiesModule, null, 2),
        },
      ],
    };
  });
}
